import React, { useState, useEffect } from "react";
import axios from "axios";
import { FiX, FiCreditCard, FiImage } from "react-icons/fi";
import toast from "react-hot-toast";
import Layout from "../Layout";
import { Link, useNavigate } from "react-router-dom";
import { DASHBOARD_ROUTE, HOME_ROUTE } from "../../../routes/web/rootRoutes";
import { FaClock } from "react-icons/fa";
import { IoIosCloseCircleOutline } from "react-icons/io";
import authManager from "../../../utils/authManager";

const PaymentOptions = ({ onClose=false, payments=[] }) => {

    const navigate = useNavigate();
    const user = authManager.getUserProfile();
    
    const [imageUrl, setImageUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState(null);

    // Check if 2+ years payment pending
    const getExtraFee = () => {
        const currentYear = new Date().getFullYear();
        const pendingYears = payments.filter(p => !p.is_paid).map(p => parseInt(p.year));
        const oldestPending = Math.min(...pendingYears);
        
        if (oldestPending && (currentYear - oldestPending) >= 2 && !user?.is_total_paid) {
            return 2500;
        }
        return 0;
    };

    const extraFee = getExtraFee();
    console.log(extraFee);
    
    const totalDue = (user?.total_due || 0) + extraFee;

    useEffect(() => {
        const fetchBankDetails = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}bank-details`
                );
                if (res.data.response) {
                    setImageUrl(res.data.image);
                } else {
                    toast.error("Bank details not available.");
                }
            } catch (error) {
                toast.error(
                    error?.response?.data?.message ||
                    "Failed to load bank details. Please try again later."
                );
            } finally {
                setLoading(false);
            }
        };

        fetchBankDetails();
    }, []);

    const openModal = (type) => {
        setModalType(type);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setModalType(null);
    };

    const handleSkipPayment = () => {
        toast.success('You can pay later from your dashboard.');
        setTimeout(() => {
            navigate(DASHBOARD_ROUTE);
        }, 1000);
    };

    return (
        <div className="flex items-center justify-center p-4">
            <div className="w-[350px] bg-white rounded-xl relative shadow-lg p-6 border border-gray-100">
                <Link to={HOME_ROUTE} className="absolute -top-3 -right-3 w-10 h-10 rounded-full flex justify-center items-center z-10 bg-red-50 shadow hover:bg-red-500 hover:text-white">
                    <IoIosCloseCircleOutline className="font-semibold" size={18}/>
                </Link>
                <h2 className="text-2xl font-semibold text-center mb-1 text-gray-800">
                    Payment Options
                </h2>
                <p className="text-center text-sm text-gray-500 mb-6">
                    Choose your preferred method
                </p>

                {loading && (
                    <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                )}

                <div className="space-y-3">
                    <button onClick={() => openModal('online')}
                        className="w-full py-3 px-4 bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium rounded-lg hover:from-emerald-600 hover:to-green-600 transition shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-base"
                    >
                        <FiCreditCard size={20} />
                        Pay through Online
                    </button>

                    <button onClick={() => openModal('bank')}
                        disabled={loading && !imageUrl}
                        className={`w-full py-3 px-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium rounded-lg hover:from-indigo-600 hover:to-purple-600 transition shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-base ${loading && !imageUrl ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <FiImage size={20} />
                        Bank Details
                    </button>
                </div>
                
                <div className="my-3">
                    <div className="flex items-center justify-between gap-3">
                        <button onClick={handleSkipPayment}
                            className="px-6 py-2 border hover:text-gray-700 text-white border-gray-300 bg-orange-500 rounded-lg hover:bg-gray-50 transition">
                            Pay Later
                        </button>

                        <button className="px-6 py-2 border text-white border-gray-300 bg-red-500 rounded-lg hover:bg-gray-50 transition">
                            ₹{totalDue}
                        </button>
                    </div>
                    {extraFee > 0 && (
                        <p className="text-xs text-red-500 text-right mt-2">
                            + ₹2500 (2 years pending re-admission fee)
                        </p>
                    )}
                </div>
                
                <p className="text-xs text-center text-gray-400 mt-5">
                    Secure & encrypted transactions
                </p>
            </div>

            {modalOpen && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50"
                    onClick={closeModal}>
                    <div className="relative bg-white rounded-xl max-w-xl w-full max-h-[90vh] overflow-auto shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button onClick={closeModal}
                            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 bg-gray-100 rounded-full p-1.5 transition z-10">
                            <FiX size={20} />
                        </button>

                        <div className="p-5">
                            {modalType === 'online' && (
                                <div className="text-center py-8">
                                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                                        Coming Soon
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        Online payment feature is under development. Stay tuned!
                                    </p>
                                </div>
                            )}

                            {modalType === 'bank' && (
                                <>
                                    <h3 className="text-lg font-medium text-gray-800 mb-3 text-center">
                                        Bank Details
                                    </h3>
                                    {imageUrl ? (
                                        <img src={imageUrl} alt="Bank details"
                                            className="w-full h-auto rounded-lg border border-gray-200" />
                                    ) : (
                                        <div className="text-center py-8 text-gray-500">
                                            <p>Bank details not available.</p>
                                            {!loading && (
                                                <button onClick={() => window.location.reload()}
                                                    className="mt-2 text-indigo-600 underline text-sm"
                                                >
                                                    Retry
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentOptions;